import React, { useEffect, useState, useRef } from "react";
import Loader from "../../../components/Loader/Loader";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import FormField from "../../../components/FormField/FormField";
import { Input, Col, Button, FormFeedback } from "reactstrap";
import { getCompanySettingServices } from "../../../actions/companyActions";
import { connect } from "react-redux";
import { Select } from "antd";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import ProfileImageCropModal from "../../../components/ImageCropper/ProfileImageCropModal";
import "./ProfileImageUpload.css";
import * as Icon from "react-feather";

import {
  getUserProfileData,
  updateUserProfileData,
} from "../../../actions/myProfileActions";
import {
  notification,
  emailRegex,
} from "../../../utils/utility";
import { buildProfileImageSrc } from "../../../utils/profileImage";

const { Option } = Select;

const validation = Yup.object().shape({
  first_name: Yup.string().required("First name is required."),
  last_name: Yup.string().required("Last name is required."),
  phone: Yup.string().required("Phone is required."),
  email: Yup.string()
    .matches(emailRegex, "Please enter valid email")
    .required("Email is required"),
  timezone_id: Yup.string().required("Timezone is required."),
});

let initialValues = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  address_line_1: "",
  city: "",
  state: "",
  zip: "",
  country_id: "",
  timezone_id: "",
  image_cropped_x: "",
  image_cropped_y: "",
  image_cropped_width: "",
  image_cropped_height: "",
  image_cropped_unit: "",
};

const MAX_PROFILE_IMAGE_SIZE = 800 * 1024;

const ProfileInfo = (props) => {
  const [formValues, setFormValues] = useState(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [timezones, setTimezones] = useState([]);

  const [profileImageUrl, setProfileImageUrl] = useState(false);
  const [selectedFile, setSelectedFile] = useState(undefined);

  const [previewUrl, setPreviewUrl] = useState(null);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [pendingCropSrc, setPendingCropSrc] = useState(null);
  const fileInputRef = useRef(null);
  const pendingCropFileRef = useRef(null);

  const croppedImage = useRef(null);
  const imageDirtyRef = useRef(false);
  const skipInitialCropChange = useRef(true);
  const removeProfileImageRef = useRef(false);

  useEffect(() => {
    const { getCompanySettingServices, getUserProfileData } = props;

    setPageLoading(true);

    getUserProfileData({
      data: {},
      onSuccess: function (resData) {
        setFormValues({
          id: resData.id,
          first_name: resData.first_name,
          last_name: resData.last_name,
          email: resData.email,
          phone: resData.phone ? resData.phone : "",
          address_line_1: resData.address_line_1,
          city: resData.city,
          state: resData.state,
          zip: resData.zip,
          country_id: resData.country
            ? resData.country.value
              ? resData.country.value
              : ""
            : "",
          timezone_id: resData.timezone_id ? resData.timezone_id : "",
          image_cropped_x: resData.image_cropped_x,
          image_cropped_y: resData.image_cropped_y,
          image_cropped_width: resData.image_cropped_width,
          image_cropped_height: resData.image_cropped_height,
          image_cropped_unit: resData.image_cropped_unit,
        });

        setProfileImageUrl(resData.profile_image);
        setPageLoading(false);
      },
      onError: function (error) {
        setPageLoading(false);
      },
    });

    getCompanySettingServices({
      data: {},
      onSuccess: function (data) {
        setPageLoading(false);
        setCountries(data.countries);
        setTimezones(data.timezones);
      },
      onError: function (error) {
        console.log("error", error);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSelectFile = (e) => {
    if (!e.target.files || e.target.files.length === 0) {
      return;
    }

    const file = e.target.files[0];

    if (!["image/jpeg", "image/png", "image/gif"].includes(file.type)) {
      notification.error("Please upload a JPG, GIF, or PNG image.");
      e.target.value = "";
      return;
    }

    if (file.size > MAX_PROFILE_IMAGE_SIZE) {
      notification.error("Image must be 800kB or smaller.");
      e.target.value = "";
      return;
    }

    if (pendingCropSrc?.startsWith("blob:")) {
      URL.revokeObjectURL(pendingCropSrc);
    }

    pendingCropFileRef.current = file;
    setPendingCropSrc(URL.createObjectURL(file));
    setCropModalOpen(true);
    e.target.value = "";
  };

  const onCropCancel = () => {
    if (pendingCropSrc?.startsWith("blob:")) {
      URL.revokeObjectURL(pendingCropSrc);
    }
    pendingCropFileRef.current = null;
    setPendingCropSrc(null);
    setCropModalOpen(false);
  };

  const onCropApply = (crop, cImage, setFieldValue) => {
    removeProfileImageRef.current = false;
    imageDirtyRef.current = true;
    skipInitialCropChange.current = false;
    croppedImage.current = cImage;
    setPreviewUrl(cImage);
    setSelectedFile(pendingCropFileRef.current);

    setFieldValue("image_cropped_x", Math.round(crop.x));
    setFieldValue("image_cropped_y", Math.round(crop.y));
    setFieldValue("image_cropped_width", Math.round(crop.width));
    setFieldValue("image_cropped_height", Math.round(crop.height));
    setFieldValue("image_cropped_unit", crop.unit || "px");

    setProfileImageUrl(cImage);

    if (pendingCropSrc?.startsWith("blob:")) {
      URL.revokeObjectURL(pendingCropSrc);
    }
    pendingCropFileRef.current = null;
    setPendingCropSrc(null);
    setCropModalOpen(false);
  };

  const onRemoveImage = (setFieldValue) => {
    removeProfileImageRef.current = true;
    setProfileImageUrl(false);
    setSelectedFile(undefined);
    setPreviewUrl(null);
    croppedImage.current = null;
    imageDirtyRef.current = true;
    skipInitialCropChange.current = true;

    setFieldValue("image_cropped_x", "");
    setFieldValue("image_cropped_y", "");
    setFieldValue("image_cropped_width", "");
    setFieldValue("image_cropped_height", "");
    setFieldValue("image_cropped_unit", "");
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  const displayImageSrc = buildProfileImageSrc(
    previewUrl || profileImageUrl,
    props.authUser?.profile_image_version
  );

  return (
    <section>
      <Loader isShowLoader={pageLoading} />
      <div>
        <div className="rd_vacationfilterpart rd_vacationfilterpart3">
          <Formik
            validationSchema={validation}
            initialValues={formValues || initialValues}
            enableReinitialize
            onSubmit={(data) => {
              const { updateUserProfileData } = props;

              const imageChanged =
                Boolean(selectedFile) ||
                (Boolean(croppedImage.current) && imageDirtyRef.current);

              if (selectedFile && !croppedImage.current) {
                notification.warning(
                  "Please crop your photo and click Apply before saving."
                );
                return;
              }

              setPageLoading(true);

              updateUserProfileData({
                data: {
                  ...data,
                  profile_image: selectedFile,
                  cropped_image: imageChanged ? croppedImage.current : undefined,
                  ...(removeProfileImageRef.current
                    ? { remove_profile_image: "E" }
                    : {}),
                },
                onSuccess: (response, updatedUser) => {
                  if (response.success) {
                    notification.success(response.message);
                  }

                  const timezoneId = localStorage.getItem("userTimezoneId");
                  if (timezoneId != data.timezone_id) {
                    const timezone = timezones.find(
                      (tz) => parseInt(tz.value) === parseInt(data.timezone_id)
                    );
                    if (timezone) {
                      localStorage.setItem(
                        "userTimezone",
                        timezone.timezone_value
                      );
                      localStorage.setItem("userTimezoneId", timezone.value);
                    }
                  }

                  if (updatedUser?.profile_image) {
                    setProfileImageUrl(updatedUser.profile_image);
                  } else {
                    setProfileImageUrl(false);
                  }

                  setSelectedFile(undefined);
                  setPreviewUrl(null);
                  croppedImage.current = null;
                  imageDirtyRef.current = false;
                  removeProfileImageRef.current = false;
                  skipInitialCropChange.current = true;

                  setPageLoading(false);
                },
                onError: (error) => {
                  notification.error(
                    "An error occured during save, Please try later"
                  );
                  setPageLoading(false);
                  console.log("company error ", error);
                },
              });
            }}
          >
            {(formProps) => {
              const {
                values,
                errors,
                touched,
                handleChange,
                setFieldTouched,
                setFieldValue,
              } = formProps;

              const initials =
                `${values.first_name?.charAt(0) || ""}${values.last_name?.charAt(0) || ""}`.toUpperCase();

              return (
                <>
                  <Form>
                    <div className="profile-image-upload">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,.jpg,.jpeg,.png,.gif"
                        onChange={onSelectFile}
                        name="profile_image"
                        id="uploadImg"
                        className="profile-image-upload__file-input"
                      />

                      <div className="profile-image-upload__row">
                        {displayImageSrc ? (
                          <img
                            src={displayImageSrc}
                            alt="Profile"
                            className="profile-image-upload__avatar"
                          />
                        ) : (
                          <div className="profile-image-upload__avatar profile-image-upload__avatar--placeholder">
                            {initials || "?"}
                          </div>
                        )}
                        <div className="profile-image-upload__actions">
                          <button
                            type="button"
                            className="profile-image-upload__btn profile-image-upload__btn--outline"
                            onClick={triggerFileSelect}
                          >
                            Update
                          </button>
                          {profileImageUrl && (
                            <button
                              type="button"
                              className="profile-image-upload__btn profile-image-upload__btn--text"
                              onClick={() => onRemoveImage(setFieldValue)}
                            >
                              <Icon.Trash2 size={16} />
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                      <p className="profile-image-upload__hint">
                        JPG, GIF or PNG. Max size 800kB.
                      </p>

                      <ProfileImageCropModal
                        isOpen={cropModalOpen}
                        imgSrc={pendingCropSrc}
                        savedCrop={null}
                        onCancel={onCropCancel}
                        onApply={(crop, cImage) =>
                          onCropApply(crop, cImage, setFieldValue)
                        }
                      />
                    </div>
                    <div className="rd_profilerd_erpart">
                      <div className="rd_vacationflex2">
                        <p>First Name *</p>
                        <div className="rd_profilethingco">
                          <FormField
                            type="text"
                            name="first_name"
                            placeholder="First Name"
                            value={values.first_name}
                            errors={errors}
                            touched={touched}
                            onChange={({ target }) => {
                              const { value } = target;
                              handleChange({
                                target: { name: "first_name", value },
                              });
                            }}
                          />
                        </div>

                        <p>Email *</p>
                        <div className="rd_profilethingco">
                          <FormField
                            type="text"
                            name="email"
                            placeholder="Email"
                            value={values.email}
                            errors={errors}
                            touched={touched}
                            onChange={({ target }) => {
                              const { value } = target;
                              handleChange({
                                target: { name: "email", value },
                              });
                            }}
                            disabled={true}
                          />
                        </div>
                      </div>

                      <div className="rd_vacationflex2">
                        <p>Last Name *</p>
                        <div className="rd_profilethingco">
                          <FormField
                            type="text"
                            name="last_name"
                            placeholder="Last Name"
                            value={values.last_name}
                            errors={errors}
                            touched={touched}
                            onChange={({ target }) => {
                              const { value } = target;
                              handleChange({
                                target: { name: "last_name", value },
                              });
                            }}
                          />
                        </div>

                        <p>Phone Number *</p>
                        <div className="rd_profilethingco">
                          <PhoneInput
                            containerClass="custom_phn_inp"
                            country={"us"}
                            value={values.phone}
                            onBlur={() => {
                              setFieldTouched(`phone`, true);
                            }}
                            onChange={(value) => {
                              handleChange({
                                target: { name: "phone", value },
                              });
                            }}
                          />
                          {errors[`phone`] && touched[`phone`] && (
                            <FormFeedback className="d-block">
                              {errors[`phone`]}
                            </FormFeedback>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="rd_profilerd_erpart rd_profilerd_erpart2">
                      <div className="rd_vacationflex2">
                        <p>Address Line 1</p>

                        <div className="rd_profilethingco">
                          <Input
                            type="textarea"
                            rows="6"
                            name="address_line_1"
                            placeholder="Address"
                            value={values.address_line_1}
                            onChange={({ target }) => {
                              const { value } = target || {};
                              handleChange({
                                target: { name: "address_line_1", value },
                              });
                            }}
                          />
                          <p className="info_txt">
                            Street address, P.O. box, Company name C/o
                          </p>
                        </div>
                      </div>
                      <div className="rd_vacationflex2">
                        <p>Address Line 2</p>

                        <div className="rd_profilethingco">
                          <Input
                            type="textarea"
                            rows="6"
                            name="address_line_1"
                            placeholder="Address"
                            value={values.address_line_1}
                            // onChange={({ target }) => {
                            //   const { value } = target || {};
                            //   handleChange({
                            //     target: { name: "address_line_1", value },
                            //   });
                            // }}
                          />
                          <p className="info_txt">
                            Apartment, suite, unit, building, floor, etc
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="rd_profilerd_erpart rd_profilerd_erpart2">
                      <div className="rd_vacationflex2">
                        <p>City</p>
                        <div className="rd_profilethingco">
                          <FormField
                            type="text"
                            name="city"
                            placeholder="City"
                            value={values.city}
                            errors={errors}
                            touched={touched}
                            onChange={({ target }) => {
                              const { value } = target;
                              handleChange({
                                target: { name: "city", value },
                              });
                            }}
                          />
                        </div>
                      </div>

                      <div className="rd_vacationflex2">
                        <p>State/Province/Region</p>
                        <div className="rd_profilethingco">
                          <FormField
                            type="text"
                            name="state"
                            placeholder="State"
                            value={values.state}
                            errors={errors}
                            touched={touched}
                            onChange={({ target }) => {
                              const { value } = target;
                              handleChange({
                                target: { name: "state", value },
                              });
                            }}
                          />
                        </div>
                      </div>

                      <div className="rd_vacationflex2">
                        <p>Zip/Postal code</p>
                        <div className="rd_profilethingco">
                          <FormField
                            type="text"
                            name="zip"
                            placeholder="Zip Code"
                            value={values.zip}
                            errors={errors}
                            touched={touched}
                            onChange={({ target }) => {
                              const { value } = target;
                              handleChange({
                                target: { name: "zip", value },
                              });
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="rd_profilerd_erpart rd_profilerd_erpart2">
                      <div className="rd_vacationflex2">
                        <p>Country</p>
                        <div className="rd_profilethingco">
                          <Select
                            showSearch
                            size="large"
                            allowClear={true}
                            className="w-100"
                            placeholder="Please select Country"
                            value={values.country_id}
                            filterOption={(input, option) =>
                              option.children
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            onChange={(value) => {
                              handleChange({
                                target: { name: "country_id", value },
                              });
                            }}
                          >
                            {(countries || []).map((country, index) => {
                              return (
                                <Option
                                  key={country.value}
                                  value={country.value}
                                >
                                  {country.label}
                                </Option>
                              );
                            })}
                          </Select>
                        </div>
                      </div>

                      <div className="rd_vacationflex2">
                        <p>Timezone *</p>
                        <div className="rd_profilethingco">
                          <div className="">
                            <FormField
                              placeholder="Select Timezone"
                              showPlaceholder={true}
                              type="select"
                              name="timezone_id"
                              errors={errors}
                              touched={touched}
                              value={values.timezone_id.toString()}
                              options={timezones}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <Col lg="12" md="12" sm="12" className="submit_btn">
                      <Button
                        type="submit"
                        className="btn btn-app col-md-2 col-lg-2 col-sm-12 mb-5"
                      >
                        Save
                      </Button>
                    </Col>
                  </Form>
                </>
              );
            }}
          </Formik>
        </div>
      </div>
    </section>
  );
};

export default connect(
  (state) => ({
    authUser: state.auth.user,
  }),
  {
    getCompanySettingServices,
    getUserProfileData,
    updateUserProfileData,
  }
)(ProfileInfo);
